import { useCallback, useRef, useState } from 'react';
import { push, replace } from 'redux-first-router';
import type { IntersectionPayload } from '@/hooks/useIntersectionListener';
import type { CompanyReportSection } from '../useSection';
import { useSection, isValidSection } from '../useSection';
import useManualScrollRestore from './useManualScrollRestore';
import usePopstateHandler from './usePopstateHandler';
import type { ANCHOR_TO_SECTION_MAPPING } from '@/constants/anchors';
import { useMount } from '@simplywallst/ui-core';
import { useCompanyReportPageUrl } from './useCompanyReportPageUrl';

export type UpdateMethod = 'push' | 'replace' | 'none';
export type CompanySubSection =
  | CompanyReportSection
  | keyof typeof ANCHOR_TO_SECTION_MAPPING;

//This hook handles the url logic for the company split component
function useReportUrlHandler() {
  const [updateMethod, setUpdateMethod] = useState<UpdateMethod>('replace');
  const companyUrl = useCompanyReportPageUrl();
  const { currentSection } = useSection();
  const handlingSection = useRef<string>();

  // I can't figure out the root cause why the browser history is duplicated
  // when moving from report outside (example: dashboard) to report page
  // So I choose to prevent it in action of the back button
  const currentHref = useRef<string>();

  useMount(() => {
    if (window.location.hash === '') {
      window.scrollTo(0, 0);
    }
    currentHref.current = window.location.href;
  });

  const onIntersectionPathUpdate = useCallback(
    (data: IntersectionPayload, shareToken: string, section?: string) => {
      if (companyUrl === '' || !section) return;

      //We want to only call this function if sections changed
      if (section === currentSection) return;

      if (handlingSection.current === section) return;
      handlingSection.current = section;

      const newUrl = `${companyUrl}${
        section === 'summary' ? '' : `/${section}`
      }${shareToken !== '' ? `?token=${shareToken}` : ''}`;
      const updateUrl =
        updateMethod === 'push'
          ? push
          : updateMethod === 'replace'
            ? replace
            : null;
      updateUrl?.(newUrl);
      setUpdateMethod('replace');
    },
    [companyUrl, currentSection, updateMethod]
  );

  /** TODO - deprecate? */
  const goToSection = useCallback(
    (section: CompanySubSection, method: UpdateMethod = 'none') => {
      setUpdateMethod(method);
      const sectionEl = document.getElementById(section);
      sectionEl?.scrollIntoView();
    },
    []
  );

  //For the back / forward buttons to jump to the right section
  //we need to set history.scrollRestoration = 'manual'
  //otherwise it jumps back to the previously saved scroll position
  useManualScrollRestore();

  usePopstateHandler(
    useCallback(
      (e) => {
        const target = e.target as Window;
        const targetHref = target?.location.href;
        if (targetHref === currentHref.current) {
          // back until the href is different
          window.history.back();
          return;
        }
        currentHref.current = target.location.href;
        const pathname = target?.location.pathname;
        const sectionName = pathname.split('/').pop();
        const section = isValidSection(sectionName) ? sectionName : 'summary';
        goToSection(section);
      },
      [goToSection]
    )
  );

  return {
    onIntersectionPathUpdate,
    goToSection,
  };
}
export default useReportUrlHandler;
